import { useRef, useState } from 'react';
import type { IJourney } from '../../../../models/journey.model';
import './style.scss';
import Details from './components/Details';
import JourneyMap from './components/JourneyMap';
import Prices from './components/Prices';
import useFetch, { DataSource, Status } from '../../../../hooks/fetch.hook';
import { JourneyDetailsApiModel } from '../../../../services/api-planera-resa/models/journey-details/journey-details.api-model';
import type { IJourneyDetails } from '../../../../models/journey-details.model';
import type { IServiceJourney } from '../../../../models/service-journey.model';
import MicromobilityHelper from '../../helpers/micromobility.helper';
import MicromobilityJourneyMap from './components/MicromobilityJourneyMap';
import { FeatureFlagService } from '../../../../services/feature-flag.service';
import { IMicromobilityLink } from '../../../../models/labs/micromobility/micromobility-link.model';

function processReceivedData(data: JourneyDetailsApiModel | undefined, journey: IJourney): IJourneyDetails | undefined {
  if (!data) return undefined;

  const mappedData = JourneyDetailsApiModel.mapToBusinessModel(data);
  if (!MicromobilityHelper.isMicromobilityJourney(journey)) return mappedData;

  const replacedLegs = [...(journey.startingMicromobilityLink?.replacesLegIndex ?? []), ...(journey.endingMicromobilityLink?.replacesLegIndex ?? [])];
  return {
    ...mappedData,
    departureAccessLink: journey.startingMicromobilityLink !== undefined ? journey.departureAccessLink : mappedData.departureAccessLink,
    arrivalAccessLink: journey.endingMicromobilityLink !== undefined || (journey.tripLegs && journey.tripLegs.length === 0) ? journey.arrivalAccessLink : mappedData.arrivalAccessLink,
    connectionLinks: mappedData.connectionLinks?.filter((x) => !replacedLegs.includes(x.journeyLegIndex)),
    tripLegs: mappedData.tripLegs?.filter((x) => !replacedLegs.includes(x.journeyLegIndex)),
  };
}

function JourneyDetails({ journey, isVisible }: { journey: IJourney; isVisible?: boolean }) {
  const journeyMapRef = useRef<HTMLDivElement>(null);
  const [selectedServiceJourney, setSelectedServiceJourney] = useState<IServiceJourney | undefined>(undefined);
  const [selectedMicromobilityLink, setSelectedMicromobilityLink] = useState<IMicromobilityLink | undefined>(undefined);

  const url = isVisible
    ? `/journeys/${journey.detailsReference}/details?includes=links&includes=ServiceJourneyCalls&includes=TripLegCoordinates&includes=ServiceJourneyCoordinates&includes=ticketsuggestions`
    : undefined;

  const [getJourneyDetails] = useFetch<JourneyDetailsApiModel | undefined>(url, !isVisible, false, {
    dataSource: DataSource.apiPlaneraResa,
    keepLastStateIfPrevented: true,
    delayInMs: 0,
  });

  const journeyDetails = processReceivedData(getJourneyDetails.data, journey);

  return (
    <div className="row travel-planner-details__column">
      {journeyDetails && journey && (
        <>
          <div className="col-12 col-md-6 order-2 w-100 mb-3 mb-md-0 travel-planner-details__column-item travel-planner-details__column-item--details">
            <Details
              journey={journey}
              journeyDetails={journeyDetails}
              journeyMapRef={journeyMapRef}
              onServiceJourneySelected={(serviceJourney) => setSelectedServiceJourney(serviceJourney)}
              onMicromobilityLinkSelected={(micromobilityLink) => setSelectedMicromobilityLink(micromobilityLink)}
            />
          </div>
          <div ref={journeyMapRef} className="col-12 col-md-6 travel-planner-details__column-break order-1 w-100 mb-3 travel-planner-details__column-item travel-planner-details__column-item--map">
            {MicromobilityHelper.isMicromobilityJourney(journey) && FeatureFlagService.enableMicromobilitySearch ? (
              <MicromobilityJourneyMap
                journey={journey}
                journeyDetails={journeyDetails}
                selectedServiceJourney={selectedServiceJourney}
                selectedMicromobilityLink={selectedMicromobilityLink}
                isLoading={getJourneyDetails.status === Status.loading}
              />
            ) : (
              <JourneyMap journey={journey} journeyDetails={journeyDetails} selectedServiceJourney={selectedServiceJourney} isLoading={getJourneyDetails.status === Status.loading} />
            )}
          </div>
          {!MicromobilityHelper.isMicromobilityJourney(journey) && (
            <div className="col-12 col-md-6 order-3 w-100 travel-planner-details__column-item travel-planner-details__column-item--prices">
              <Prices journey={journey} journeyDetails={journeyDetails} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default JourneyDetails;
